/**
 * 63.1u, the bots' smoke dispensers (the owner: "could also be used when under a lot of rocket pressure or pressure beyond
 * bullet impacts … bots do a pretty good withdrawl in smoke currently, or at least throw smoke between bot and whoever is
 * getting good shots in on them, applying the same thing to transit vehicle would be a good start"; and "can be used for
 * intentional contested assaults"). A bot driving a vehicle with a charge ready pops it (the ring's front turned to the
 * threat, as its smoke on withdrawal goes between it and the shooter):
 * - **under rocket pressure:** an enemy rocket in flight within VEHICLE_SMOKE.rocketPeople of the hull, closing — toward it;
 * - **hit beyond bullets:** the hull at or under VEHICLE_SMOKE.hurtShare of its health with an enemy its side knows of within
 *   VEHICLE_SMOKE.nearPeople — toward the nearest;
 * - **a contested approach:** driving at an objective its side does not hold, within VEHICLE_SMOKE.approachPeople, ahead of
 *   it, with an enemy its side knows of within the support band of it — toward the objective.
 * Pure in the state.
 */
import type { GameState, MatchInputBody, PlayerState, StructureState, Vec3 } from '../types';
import { VEHICLE_SMOKE } from '../constants';
import { canPopSmoke } from '../core/vehicleSmoke';
import { teamPicture } from '../core/picture';
import { structureHealth } from '../core/body';
import { activeSites } from '../core/capturePoints';
import { getStructureWorldPosition } from '../core/gameState';
import { SUPPORT_POSITION, rifleEffectivePeople } from '../core/support';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { vecDot, vecLength, vecNormalize, vecSub } from '../math';
import { dacos } from '../core/dmath';

export function vehicleSmokeFor(state: GameState, unit: PlayerState, vehicle: StructureState, tick: number): MatchInputBody | null {
  if (!canPopSmoke(state, unit, tick).ok) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const hull = getStructureWorldPosition(vehicle, state.planets);
  const P = state.planets[vehicle.planetIndex];
  const arc = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * P.radius;
  const pop = (to: Vec3): MatchInputBody => ({ kind: 'vehicleSmoke', unitId: unit.id, toward: vecSub(to, hull) });
  // under rocket pressure: an enemy rocket closing on the hull
  for (const b of state.bullets ?? []) {
    if (!b.blast) continue;
    const owner = state.players.find(p => p.id === b.ownerId);
    if (!owner || owner.teamIndex === unit.teamIndex) continue;
    const off = vecSub(hull, b.pos);
    if (vecLength(off) <= VEHICLE_SMOKE.rocketPeople * person && vecDot(b.vel, off) > 0) return pop(b.pos);
  }
  const known = teamPicture(state, unit.teamIndex, tick).filter(k => k.planetIndex === vehicle.planetIndex);
  // hit beyond bullets: the hull hurt, an enemy its side knows of near
  if (vehicle.health <= VEHICLE_SMOKE.hurtShare * structureHealth(vehicle.type)) {
    let near: { pos: Vec3; d: number } | null = null;
    for (const k of known) { const d = arc(k.surfaceDir, vehicle.surfaceDir); if (d <= VEHICLE_SMOKE.nearPeople * person && (!near || d < near.d)) near = { pos: k.pos, d }; }
    if (near) return pop(near.pos);
  }
  // a contested approach: driving at an objective it does not hold, ahead and near, with an enemy known at it
  const heading = vehicle.drive?.heading;
  if (heading && Math.abs(vehicle.drive?.speed ?? 0) > 0) {
    const band = SUPPORT_POSITION.share * rifleEffectivePeople() * (1 + SUPPORT_POSITION.tolerance) * person;
    for (const s of activeSites(state)) {
      if (s.owner === unit.teamIndex || s.planetIndex !== vehicle.planetIndex) continue;
      if (arc(s.surfaceDir, vehicle.surfaceDir) > VEHICLE_SMOKE.approachPeople * person) continue;
      const at = { x: P.position.x + s.surfaceDir.x * P.radius, y: P.position.y + s.surfaceDir.y * P.radius, z: P.position.z + s.surfaceDir.z * P.radius };
      if (vecDot(vecSub(at, hull), heading) <= 0) continue;   // (behind it)
      if (known.some(k => arc(k.surfaceDir, s.surfaceDir) <= band)) return pop(at);
    }
  }
  return null;
}
